import { useCallback, useEffect, useRef, useState } from 'react';
import { onboardingApi, OnboardingError } from './api';

const TOKEN_KEY = 'lms_talent_draft_token';
const AUTOSAVE_DELAY = 1100;

const readStoredToken = () => {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

const storeToken = (token) => {
  try {
    window.localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* private browsing — the resume link is the fallback */
  }
};

const clearStoredToken = () => {
  try {
    window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* nothing we can do */
  }
};

/**
 * Owns the whole draft: loading, local edits, autosave and resume.
 *
 * Local state is authoritative while typing so the UI never stutters waiting
 * for the network. The server is the source of truth for completion and for
 * validation notes, which arrive on each save response.
 */
export function useOnboardingDraft() {
  const [status, setStatus] = useState('loading'); // loading | ready | failed
  const [loadError, setLoadError] = useState(null);
  const [config, setConfig] = useState(null);
  const [token, setToken] = useState(null);
  const [profile, setProfile] = useState(null);
  const [sections, setSections] = useState({});
  const [documents, setDocuments] = useState([]);
  const [completion, setCompletion] = useState(0);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [saveError, setSaveError] = useState(null);
  const [sectionErrors, setSectionErrors] = useState({});

  // Refs so the debounced flush always sees current values without
  // re-creating timers on every keystroke.
  const pendingRef = useRef({});
  const timerRef = useRef(null);
  const tokenRef = useRef(null);
  const inflightRef = useRef(null);

  useEffect(() => { tokenRef.current = token; }, [token]);

  const applyDraft = useCallback((payload) => {
    setProfile(payload.profile);
    setSections(payload.sections || {});
    setDocuments(payload.documents || []);
    setCompletion(payload.profile?.completion_percent || 0);
  }, []);

  // --- bootstrap -------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const configResponse = await onboardingApi.getConfig();
        if (cancelled) return;
        setConfig(configResponse.config);

        const params = new URLSearchParams(window.location.search);
        const resumeToken = params.get('resume');
        const campusIdRaw = params.get('campus');
        const campusId = campusIdRaw ? Number(campusIdRaw) : null;
        const urlCampus = Number.isInteger(campusId) && campusId > 0 ? campusId : null;

        const tryResume = async (token) => {
          const draft = await onboardingApi.resume(token);
          if (cancelled) return null;

          const draftCampus = draft.profile?.campus_id
            ? Number(draft.profile.campus_id)
            : null;

          // Principal link for a different campus → do not reuse this draft.
          if (urlCampus && draftCampus && draftCampus !== urlCampus) {
            return null;
          }

          // Draft has no campus yet, but the link does → stamp it on.
          if (urlCampus && !draftCampus) {
            await onboardingApi.attachCampus(token, urlCampus);
            return onboardingApi.resume(token);
          }

          return draft;
        };

        // Prefer an explicit resume link, then a stored draft.
        const candidates = [resumeToken, readStoredToken()].filter(Boolean);
        for (const candidate of candidates) {
          try {
            const draft = await tryResume(candidate);
            if (cancelled) return;
            if (draft) {
              setToken(candidate);
              storeToken(candidate);
              applyDraft(draft);
              setStatus('ready');
              return;
            }
          } catch (err) {
            if (err.status === 404) {
              if (candidate === readStoredToken()) clearStoredToken();
              continue;
            }
            throw err;
          }
        }

        // Fresh journey for this campus (or generic if no campus in the URL).
        const started = await onboardingApi.start(urlCampus);
        if (cancelled) return;
        setToken(started.draft_token);
        storeToken(started.draft_token);
        applyDraft(started);
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        setLoadError(err instanceof OnboardingError ? err.message : 'Something went wrong.');
        setStatus('failed');
      }
    })();

    return () => { cancelled = true; };
  }, [applyDraft]);

  // --- saving ----------------------------------------------------------
  const pushSection = useCallback(async (sectionKey, data, options = {}) => {
    const activeToken = tokenRef.current;
    if (!activeToken) return { ok: false };

    setSaveState('saving');
    try {
      const response = await onboardingApi.saveSection(activeToken, sectionKey, data, options);

      setCompletion(response.completion_percent ?? 0);
      setSections((current) => ({
        ...current,
        [sectionKey]: {
          ...(current[sectionKey] || {}),
          data,
          is_complete: response.isComplete,
        },
      }));
      setSectionErrors((current) => ({ ...current, [sectionKey]: response.errors || {} }));
      setSaveState('saved');
      setSaveError(null);

      return { ok: true, isComplete: response.isComplete, errors: response.errors || {} };
    } catch (err) {
      setSaveState('error');
      setSaveError(err.message);
      return { ok: false, error: err };
    }
  }, []);

  const flush = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const pending = pendingRef.current;
    pendingRef.current = {};

    const keys = Object.keys(pending);
    if (keys.length === 0) return;

    for (const key of keys) {
      await pushSection(key, pending[key]);
    }
  }, [pushSection]);

  /** Local edit + debounced autosave. */
  const updateSection = useCallback((sectionKey, updater) => {
    setSaveState('saving');
    setSections((current) => {
      const existing = current[sectionKey]?.data || {};
      const nextData = typeof updater === 'function' ? updater(existing) : { ...existing, ...updater };

      pendingRef.current[sectionKey] = nextData;

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => { flush(); }, AUTOSAVE_DELAY);

      return { ...current, [sectionKey]: { ...(current[sectionKey] || {}), data: nextData } };
    });
  }, [flush]);

  /** Save immediately and validate strictly — used by Continue. */
  const commitSection = useCallback(async (sectionKey, currentStep) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const data = pendingRef.current[sectionKey]
      ?? sections[sectionKey]?.data
      ?? {};
    delete pendingRef.current[sectionKey];

    return pushSection(sectionKey, data, { strict: true, currentStep });
  }, [pushSection, sections]);

  /**
   * Re-validate a section and surface field notes — used when jumping from
   * Review "still to finish" chips so missing inputs light up immediately.
   */
  const revealSectionGaps = useCallback(async (sectionKey) => {
    const activeToken = tokenRef.current;
    if (!activeToken) return { ok: false, errors: {} };

    const data = pendingRef.current[sectionKey]
      ?? sections[sectionKey]?.data
      ?? {};

    try {
      const response = await onboardingApi.saveSection(activeToken, sectionKey, data, {
        strict: true,
        currentStep: sectionKey,
      });
      const errors = response.errors || {};
      setSectionErrors((current) => ({ ...current, [sectionKey]: errors }));
      setSections((current) => ({
        ...current,
        [sectionKey]: {
          ...(current[sectionKey] || {}),
          data,
          is_complete: response.isComplete,
        },
      }));
      if (response.completion_percent != null) {
        setCompletion(response.completion_percent);
      }
      return { ok: true, errors, isComplete: response.isComplete };
    } catch (err) {
      return { ok: false, errors: {}, error: err };
    }
  }, [sections]);

  const skipSection = useCallback(async (sectionKey) => {
    const activeToken = tokenRef.current;
    if (!activeToken) return { ok: false };
    setSaveState('saving');
    try {
      const response = await onboardingApi.skipSection(activeToken, sectionKey);
      setCompletion(response.completion_percent ?? 0);
      setSections((current) => ({
        ...current,
        [sectionKey]: { data: { skipped: true }, is_complete: true, skipped: true },
      }));
      setSaveState('saved');
      return { ok: true };
    } catch (err) {
      setSaveState('error');
      setSaveError(err.message);
      return { ok: false, error: err };
    }
  }, []);

  // Flush anything outstanding if the tab is closed or backgrounded.
  useEffect(() => {
    const handleHide = () => {
      if (Object.keys(pendingRef.current).length > 0) flush();
    };
    const handleBeforeUnload = (event) => {
      if (Object.keys(pendingRef.current).length > 0) {
        flush();
        event.preventDefault();
        event.returnValue = '';
      }
    };
    document.addEventListener('visibilitychange', handleHide);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      document.removeEventListener('visibilitychange', handleHide);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [flush]);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (inflightRef.current) inflightRef.current.abort();
  }, []);

  // --- media -----------------------------------------------------------
  const uploadPhoto = useCallback(async (file) => {
    const activeToken = tokenRef.current;
    if (!activeToken) return { ok: false };
    try {
      const response = await onboardingApi.uploadPhoto(activeToken, file);
      setProfile((current) => ({ ...current, photo_url: response.photo_url }));
      return { ok: true, url: response.photo_url };
    } catch (err) {
      return { ok: false, error: err };
    }
  }, []);

  const removePhoto = useCallback(async () => {
    const activeToken = tokenRef.current;
    if (!activeToken) return;
    try {
      await onboardingApi.removePhoto(activeToken);
      setProfile((current) => ({ ...current, photo_url: null }));
    } catch {
      /* leaving the photo in place is harmless */
    }
  }, []);

  const uploadDocument = useCallback(async (file, documentType) => {
    const activeToken = tokenRef.current;
    if (!activeToken) return { ok: false };
    try {
      const response = await onboardingApi.uploadDocument(activeToken, file, documentType);
      setDocuments((current) => [...current, response.document]);
      return { ok: true, document: response.document };
    } catch (err) {
      return { ok: false, error: err };
    }
  }, []);

  const removeDocument = useCallback(async (id) => {
    const activeToken = tokenRef.current;
    if (!activeToken) return;
    try {
      await onboardingApi.removeDocument(activeToken, id);
      setDocuments((current) => current.filter((d) => d.id !== id));
    } catch {
      /* keep the row; the file is still listed server-side */
    }
  }, []);

  // --- finishing --------------------------------------------------------
  const submit = useCallback(async () => {
    await flush();
    const activeToken = tokenRef.current;
    try {
      const response = await onboardingApi.submit(activeToken);
      clearStoredToken();
      return { ok: true, applicationId: response.applicationId, status: response.status };
    } catch (err) {
      return { ok: false, error: err };
    }
  }, [flush]);

  const getSectionData = useCallback(
    (sectionKey) => sections[sectionKey]?.data || {},
    [sections]
  );

  return {
    status,
    loadError,
    config,
    token,
    profile,
    sections,
    documents,
    completion,
    saveState,
    saveError,
    sectionErrors,
    getSectionData,
    updateSection,
    commitSection,
    revealSectionGaps,
    skipSection,
    flush,
    uploadPhoto,
    removePhoto,
    uploadDocument,
    removeDocument,
    submit,
  };
}
